import {
  ColumnApiModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  CsvExportModule,
  CustomFilterModule,
  ModuleRegistry,
  RenderApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
  ColDef,
} from 'ag-grid-enterprise';
import type { GridReadyEvent } from 'ag-grid-community';
import { RowApiModule } from 'ag-grid-community';
import { getFindingSpecs } from 'Apis/library';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useI18n } from 'Hooks/useI18n';
import { ContentfulContext } from 'Rdx/contexts/contentfulContext';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { DEFAULT_COLUMN_STATE } from '../AgFindingsGrid/AgFindingsGrid.config';
import { getFindingsColumns } from '../AgFindingsGrid/getFindingsColumns';
import { useFindingsServerSideDataSource } from '../AgFindingsGrid/FindingsServerSideDataSource';
import { EmptyState } from 'Components/molecules/EmptyState';
import { RootState } from 'Rdx/store';
import { ConnectorDto, FindingSpecDto, FindingsInsightsDto, KIND } from '@ampsec/platform-client';
import EngageIcon from 'Assets/icons/engagement.svg';
import '../AgFindingsGrid/styles.scss';
import { AgGridBase, useRefreshServerSideWhen, useStableServerSideDatasource } from '../AgGridBase';
import { findingsOrgFilterFromOrgModel } from 'Core-utils/helpers/orgModelFilters';
import {
  normalizeFindingsFilterCriteria,
  userAssociationFilterFromParam,
  USER_ASSOCIATION_FILTER_KEY,
} from 'Core-utils/helpers/findingsUserAssociationFilter';
import {
  USER_NAME_COLUMN_FILTER_TYPE,
  USER_NAME_COLUMN_ID,
  type UserNameColumnFilterModel,
} from '../AgFindingsGrid/userNameColumnFilter';
import { DEFAULT_BASE_COL_DEF } from '../AgGridBase/AgGridBase.config';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  RowApiModule,
  TextFilterModule,
  TooltipModule,
  SetFilterModule,
  CustomFilterModule,
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  CsvExportModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
]);

/**
 * Converts the `defaultFilter` prop shape (Record<string, string[]>) into an AG-Grid
 * filter model so the grid initialises with those filters pre-applied.
 */
function buildInitialFilterModel(
  defaultFilter: Record<string, string[]>,
  userAssociated?: string
): Record<string, any> {
  const filterModel: Record<string, any> = {};
  Object.entries(defaultFilter).forEach(([key, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      filterModel[key] = { filterType: 'set', values };
    }
  });

  if (userAssociationFilterFromParam(userAssociated)) {
    const userNameFilter: UserNameColumnFilterModel = {
      filterType: USER_NAME_COLUMN_FILTER_TYPE,
      mode: 'anyUser',
    };
    filterModel[USER_NAME_COLUMN_ID] = userNameFilter;
  }

  return filterModel;
}

/**
 * `finding_kind` is the only default-filter column whose filter component is omitted from
 * column defs until `findingKindOptions` loads (see getFindingsColumns). AG-Grid ignores
 * setFilterModel entries for columns without an active filter.
 */
function areDefaultFilterPrerequisitesReady(
  defaultFilter: Record<string, string[]>,
  findingKindOptions: string[]
): boolean {
  const needsFindingKind = Array.isArray(defaultFilter.finding_kind) && defaultFilter.finding_kind.length > 0;
  return !needsFindingKind || findingKindOptions.length > 0;
}

export interface ModalFindingsGridProps {
  defaultFilter?: Record<string, string[]>;
  /** Base API filter criteria (e.g. user_id presence). Merged with org context filters. */
  filter?: Record<string, unknown>;
  tableBodyHeight?: string;
  emptyStateDescription?: string;
}

const STABLE_DEFAULT_COL_DEF = {
  ...DEFAULT_BASE_COL_DEF,
  resizable: false,
} as ColDef<FindingsInsightsDto>;

/**
 * A self-contained AG-Grid findings grid designed exclusively for modal use.
 *
 * Unlike AgFindingsGrid, this component never reads from or writes to URL params.
 * All filter/sort state lives inside the AG-Grid instance itself. The `defaultFilter`
 * prop is converted to an AG-Grid filter model and applied once on grid ready, then
 * the user can freely refine or clear filters for the duration of the modal session.
 * Unmounting (on modal close) discards all state with no URL side-effects.
 */
const ModalFindingsGrid: React.FC<ModalFindingsGridProps> = ({
  defaultFilter = {},
  filter,
  tableBodyHeight,
  emptyStateDescription = 'No Findings match your filters.',
}) => {
  const gridRef = useRef<any>(null);

  const userAssociatedFromFilter = filter?.[USER_ASSOCIATION_FILTER_KEY];
  const userAssociatedParam =
    typeof userAssociatedFromFilter === 'string'
      ? userAssociatedFromFilter
      : Array.isArray(userAssociatedFromFilter)
      ? userAssociatedFromFilter[0]
      : undefined;

  // Capture the initial filter model once on mount so onGridReady has a stable reference.
  // defaultFilter is expected to be stable (passed from normalizeDefaultFilter).
  const initialFilterModelRef = useRef(buildInitialFilterModel(defaultFilter, userAssociatedParam));

  const { formatDisplayLabel } = useI18n();
  const { contentfulData } = useContext(ContentfulContext);
  const organization = useSelector((state: RootState) => state.orgModel.org);
  const { pidProviderMap, installedConnectorMap } = useSelector((state: RootState) => state);
  const contentfulProviderMap = contentfulData?.providerNameIntegrationMap;

  const findingSpecTransformer = useCallback((dataItem: FindingSpecDto[]) => {
    return _.uniq((dataItem ?? []).map((spec) => spec.eventType));
  }, []);

  const { data: findingKindOptionsRaw } = useApiObjectData<FindingSpecDto[], string[]>(
    KIND.FINDING_SPECS,
    getFindingSpecs,
    findingSpecTransformer,
    { limit: 250 }
  );
  const findingKindOptions = Array.isArray(findingKindOptionsRaw) ? findingKindOptionsRaw : [];

  const [providerFilterOptions, setProviderFilterOptions] = useState<string[]>([]);
  const [providerFilterOptionToProviderKeyMap, setProviderFilterOptionToProviderKeyMap] = useState<
    Record<string, { displayValue?: string }>
  >({});

  const filterCriteria = useMemo(
    () =>
      normalizeFindingsFilterCriteria({
        ...findingsOrgFilterFromOrgModel(organization),
        ...(filter ?? {}),
      }),
    [filter, organization]
  );


  useEffect(() => {
    if (!pidProviderMap || !contentfulProviderMap) {
      return;
    }
    const options: string[] = [];
    const map: Record<string, { displayValue?: string }> = {};
    Object.values(installedConnectorMap || {}).forEach(
      (connector: ConnectorDto & { provider?: { displayValue?: string } }) => {
        if (!connector?.pid) {
          return;
        }
        const provider = pidProviderMap?.[connector.pid];
        const displayValue = provider?.displayValue;
        const uiName = contentfulProviderMap?.[displayValue as string]?.name ?? displayValue;
        options.push(uiName);
        map[uiName] = { displayValue };
      }
    );
    setProviderFilterOptions([...options, 'Custom'].filter(Boolean));
    setProviderFilterOptionToProviderKeyMap(map);
  }, [pidProviderMap, contentfulProviderMap, installedConnectorMap]);

  const providerDisplayValueMap = useCallback(
    (selectedValues: string[]) =>
      selectedValues.map((name) => providerFilterOptionToProviderKeyMap[name]?.displayValue ?? name),
    [providerFilterOptionToProviderKeyMap]
  );

  const handleRowCountChange = useCallback((filtered: number, total: number) => {
    if (total === 0) {
      gridRef.current?.api?.showNoRowsOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, []);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | undefined>();
  const { datasource } = useFindingsServerSideDataSource({
    filterCriteria,
    providerDisplayValueMap: providerFilterOptions.length > 0 ? providerDisplayValueMap : undefined,
    onRowCountChange: handleRowCountChange,
    onLastUpdatedAtChange: setLastUpdatedAt
  });

  const stableDatasource = useStableServerSideDatasource(datasource);

  useRefreshServerSideWhen(gridRef, organization);

  const providerLogoMap = useMemo(() => {
    const integrations = contentfulData?.integrations;
    if (!integrations) {
      return {};
    }
    return Object.keys(integrations).reduce((acc: Record<string, string>, key: string) => {
      const url = (integrations as any)?.[key]?.logo?.fields?.file?.url;
      if (url) {
        acc[(integrations as any)[key].providerKey] = `https:${url}`;
      }
      return acc;
    }, {});
  }, [contentfulData?.integrations]);

  const agGridColumns = useMemo(
    () =>
      getFindingsColumns({
        formatDisplayLabel,
        disableFilters: false,
        providerFilterOptions,
        findingKindOptions,
        contentfulProviderMap: contentfulProviderMap as Record<string, { name?: string }>,
        providerLogoMap,
        omitUserColumn: false,
      }),
    [formatDisplayLabel, providerFilterOptions, findingKindOptions, contentfulProviderMap, providerLogoMap]
  );

  // finding_kind (and other async set filters) are disabled in column defs until their option
  // lists load. Mounting the grid before then causes onGridReady's setFilterModel to silently
  // skip those keys — Vulnerabilities is affected because it only filters on finding_kind.
  const canMountGrid = useMemo(
    () => areDefaultFilterPrerequisitesReady(defaultFilter, findingKindOptions),
    [defaultFilter, findingKindOptions]
  );

  const onGridReady = useCallback((event: GridReadyEvent<FindingsInsightsDto>) => {
    event.api.applyColumnState({
      state: DEFAULT_COLUMN_STATE,
      applyOrder: true,
    });
    // Apply defaultFilter as the initial AG-Grid filter model. AG-Grid then passes these
    // filters to the server-side datasource automatically on first load.
    event.api.setFilterModel(initialFilterModelRef.current);
  }, []);

  return (
    <AgGridBase.Root fullWidth>
      <AgGridBase.Header hidden lastUpdatedAt={lastUpdatedAt} />
      {agGridColumns && agGridColumns.length > 0 && canMountGrid && (
        <AgGridBase.Table<FindingsInsightsDto>
          ref={gridRef}
          columnDefs={agGridColumns}
          dataMode="server"
          serverSideDatasource={stableDatasource}
          showSidebar={false}
          movableColumns={false}
          bodyHeight={tableBodyHeight}
          defaultColDef={STABLE_DEFAULT_COL_DEF}
          agGridProps={{
            getRowId: (params) => String(params.data?.id ?? ''),
            suppressAggFuncInHeader: true,
            onGridReady,
            noRowsOverlayComponent: EmptyState,
            noRowsOverlayComponentParams: {
              ImageSrc: EngageIcon,
              noDescription: false,
              description: emptyStateDescription,
            },
          }}
        />
      )}
    </AgGridBase.Root>
  );
};

export default ModalFindingsGrid;
